/**
 * StatusBadge Component
 *
 * Displays document processing status with appropriate color coding.
 * Shared between DocumentList and DocumentUpload pages.
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { DocumentStatus } from '@/api/endpoints/documents';

interface StatusMeta {
  className: string;
  labelKey: string;
  /** True for statuses that carry a meaningful error_message to surface. */
  isFailure: boolean;
}

// Record<DocumentStatus, …> makes this exhaustive: the compiler forces an
// entry — including `isFailure` — for every status in the union, so adding a
// new failure variant can't silently skip the reason-surfacing path (which a
// hand-maintained Set of statuses would).
const STATUS_META: Record<DocumentStatus, StatusMeta> = {
  processing: {
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    labelKey: 'documents.list.status.processing',
    isFailure: false,
  },
  ready: {
    className: 'bg-green-100 text-green-800 border-green-200',
    labelKey: 'documents.list.status.ready',
    isFailure: false,
  },
  error: {
    className: 'bg-red-100 text-red-800 border-red-200',
    labelKey: 'documents.list.status.error',
    isFailure: true,
  },
  partial_error: {
    className: 'bg-orange-100 text-orange-800 border-orange-200',
    labelKey: 'documents.list.status.partial_error',
    isFailure: true,
  },
  quarantined: {
    className: 'bg-red-100 text-red-800 border-red-200',
    labelKey: 'documents.list.status.quarantined',
    isFailure: true,
  },
  pending_approval: {
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    labelKey: 'documents.list.status.pending_approval',
    isFailure: false,
  },
  superseded: {
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    labelKey: 'documents.list.status.superseded',
    isFailure: false,
  },
};

/** Whether a status carries a surfaced failure reason. Single source of truth. */
export function isFailureStatus(status: DocumentStatus | null | undefined): boolean {
  return status ? (STATUS_META[status]?.isFailure ?? false) : false;
}

interface StatusBadgeProps {
  status: DocumentStatus | null | undefined;
  /**
   * Server-provided failure reason (DocumentResponse.error_message). On a
   * failure status it's rendered as a visible line beneath the badge so a user
   * browsing the document LIST sees WHY a document failed — not just a red
   * "Error" label.
   *
   * Rendered inline (not a `title` tooltip) on purpose: the primary platform is
   * an iOS Safari PWA, where `title` never fires on tap/long-press — a bedside
   * nurse on iPad would see the colour but no reason. `title` on the inline line
   * only reveals the overflow of a truncated message on pointer devices.
   *
   * The upload flow and the detail page already display the reason, so the list
   * was the one place it never reached the user.
   */
  errorMessage?: string | null;
  /**
   * Whether the viewing user can ACT on a failure (owner/editor — the people
   * the Retry button renders for). Statuses are audience-aware for
   * `partial_error` ONLY: that state means the document IS searchable (chunks
   * embedded, hybrid search works) but its protocol linking is incomplete — a
   * defect only an owner/editor can fix (Retry / re-upload).
   *
   *   - canAct: "Partial Error" badge + an ACTIONABLE line (the raw backend
   *     linker detail moves to the tooltip — "linker produced no triples
   *     (dispatched=1, empty=1…)" tells a non-engineer nothing).
   *   - !canAct: rendered as "Ready" — for a reader the document works; an
   *     internal pipeline detail they cannot act on is noise, not status.
   *
   * `error` and `quarantined` stay visible to everyone: those documents do
   * not work at all, for anyone. Defaults to true (owner-facing surfaces).
   */
  canAct?: boolean;
}

export function StatusBadge({ status, errorMessage, canAct = true }: StatusBadgeProps) {
  const { t } = useTranslation();

  // Null safety: show "Unknown" for null/undefined/unrecognized statuses
  if (!status || !STATUS_META[status]) {
    return (
      <Badge className="bg-gray-100 text-gray-600 border-gray-200" variant="outline">
        {t('documents.list.status.unknown')}
      </Badge>
    );
  }

  // Audience-aware partial_error (see canAct above): readers who cannot act
  // see the state the document is in FOR THEM — searchable, i.e. ready.
  const effectiveStatus: DocumentStatus =
    status === 'partial_error' && !canAct ? 'ready' : status;

  const meta = STATUS_META[effectiveStatus];
  // partial_error swaps the raw pipeline detail for an actionable line; the
  // verbatim backend message stays reachable via the tooltip.
  const detail = errorMessage?.trim();
  const reason = !meta.isFailure
    ? undefined
    : effectiveStatus === 'partial_error'
      ? t(
          'documents.list.status.partialErrorAction',
          'Processing incomplete — may not appear in protocol search. Use Retry to reprocess.'
        )
      : detail;
  const badge = (
    <Badge className={meta.className} variant="outline">
      {t(meta.labelKey)}
    </Badge>
  );

  if (!reason) {
    return badge;
  }

  return (
    <span className="inline-flex flex-col items-start gap-0.5 min-w-0">
      {badge}
      <span className="text-xs text-red-600 truncate max-w-[14rem]" title={detail || reason}>
        {reason}
      </span>
    </span>
  );
}
